/**
 * callRecordingDB.js
 *
 * IndexedDB utility for storing call recording chunks offline.
 * Each recording session is identified by a unique callId.
 * Chunks are appended during the call; on call end they are merged
 * into a single Blob and marked as "pending" for upload.
 *
 * Schema:
 *   Store "chunks"  → { id (auto), callId, index, blob, createdAt }
 *   Store "recordings" → { callId (PK), corpId, projectId, userId, ticketId,
 *                          chatThreadId, aiTrainingChatThreadId, aiOperatorTicketId,
 *                          callStartTime, filename, blob, status, createdAt, updatedAt }
 *   Store "callMeta"   → { callId (PK), ...meta, savedAt }
 *
 * status: "pending" | "uploading" | "uploaded" | "failed"
 */

const DB_NAME = 'CallRecordingDB';
const DB_VERSION = 2;
const STORE_CHUNKS = 'chunks';
const STORE_RECORDINGS = 'recordings';

// ─── helpers ────────────────────────────────────
const STORE_CALL_META = 'callMeta';

let dbInstance = null;

const openDB = () =>
    new Promise((resolve, reject) => {
        // Reuse existing connection if still open
        if (dbInstance) {
            try {
                // Test if connection is still valid by checking name
                if (dbInstance.name === DB_NAME) {
                    return resolve(dbInstance);
                }
            } catch {
                dbInstance = null;
            }
        }

        const req = indexedDB.open(DB_NAME, DB_VERSION);

        req.onupgradeneeded = (e) => {
            const db = e.target.result;

            if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
                const chunkStore = db.createObjectStore(STORE_CHUNKS, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
                chunkStore.createIndex('byCallId', 'callId', { unique: false });
            }

            if (!db.objectStoreNames.contains(STORE_RECORDINGS)) {
                db.createObjectStore(STORE_RECORDINGS, { keyPath: 'callId' });
            }

            if (!db.objectStoreNames.contains(STORE_CALL_META)) {
                db.createObjectStore(STORE_CALL_META, { keyPath: 'callId' });
            }
        };

        req.onsuccess = () => {
            dbInstance = req.result;
            // Reset cached instance if browser closes the connection
            dbInstance.onclose = () => { dbInstance = null; };
            dbInstance.onversionchange = () => {
                dbInstance.close();
                dbInstance = null;
            };
            resolve(dbInstance);
        };
        req.onerror = () => reject(req.error);
    });

const withStore = async (storeName, mode = 'readonly') => {
    const db = await openDB();
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    return { db, tx, store };
};

const requestToPromise = (req) =>
    new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

// ─── Chunk operations ───────────────────────────

/**
 * Append a single audio chunk (Blob) for a given callId.
 */
export const addChunk = async (callId, blob, index) => {
    const { store } = await withStore(STORE_CHUNKS, 'readwrite');
    return requestToPromise(
        store.add({ callId, index, blob, createdAt: Date.now() })
    );
};

/**
 * Get all chunks for a callId, sorted by index.
 */
export const getChunksByCallId = async (callId) => {
    const { store } = await withStore(STORE_CHUNKS, 'readonly');
    const idx = store.index('byCallId');
    const chunks = await requestToPromise(idx.getAll(callId));
    return chunks.sort((a, b) => a.index - b.index);
};

/**
 * Delete all chunks for a callId (after merge).
 */
export const deleteChunksByCallId = async (callId) => {
    const { store, tx } = await withStore(STORE_CHUNKS, 'readwrite');
    const idx = store.index('byCallId');
    const keys = await requestToPromise(idx.getAllKeys(callId));
    keys.forEach((k) => store.delete(k));
    return new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
    });
};

// ─── Recording operations ───────────────────────

/**
 * Merge chunks into a single Blob and save as a "pending" recording.
 */
export const finalizeRecording = async (callId, meta = {}) => {
    const chunks = await getChunksByCallId(callId);
    if (!chunks.length) return null;

    const blob = new Blob(
        chunks.map((c) => c.blob),
        { type: 'audio/webm' }
    );

    const recording = {
        callId,
        corpId: meta.corpId || '',
        projectId: meta.projectId || '',
        userId: meta.userId || '',
        ticketId: meta.ticketId || '',
        chatThreadId: meta.chatThreadId || '',
        aiTrainingChatThreadId: meta.aiTrainingChatThreadId || '',
        aiOperatorTicketId: meta.aiOperatorTicketId || '',
        callStartTime: meta.callStartTime || null,
        filename: meta.filename || `recording_${callId}.webm`,
        blob,
        status: 'pending',
        createdAt: Date.now(),
        updatedAt: Date.now(),
    };

    const { store } = await withStore(STORE_RECORDINGS, 'readwrite');
    await requestToPromise(store.put(recording));

    // Clean up chunks
    await deleteChunksByCallId(callId);

    return recording;
};

/**
 * Get a single recording by callId.
 */
export const getRecording = async (callId) => {
    const { store } = await withStore(STORE_RECORDINGS, 'readonly');
    return requestToPromise(store.get(callId));
};

/**
 * Get all recordings with a given status.
 */
export const getRecordingsByStatus = async (status) => {
    const { store } = await withStore(STORE_RECORDINGS, 'readonly');
    const all = await requestToPromise(store.getAll());
    return all.filter((r) => r.status === status);
};

/**
 * Get all pending or failed recordings (for sync).
 */
export const getPendingRecordings = async () => {
    const { store } = await withStore(STORE_RECORDINGS, 'readonly');
    const all = await requestToPromise(store.getAll());
    return all.filter((r) => r.status === 'pending' || r.status === 'failed');
};

/**
 * Update recording status.
 */
export const updateRecordingStatus = async (callId, status) => {
    const { store } = await withStore(STORE_RECORDINGS, 'readwrite');
    const rec = await requestToPromise(store.get(callId));
    if (!rec) return null;
    rec.status = status;
    rec.updatedAt = Date.now();
    return requestToPromise(store.put(rec));
};

/**
 * Delete a recording after successful upload.
 */
export const deleteRecording = async (callId) => {
    const { store } = await withStore(STORE_RECORDINGS, 'readwrite');
    return requestToPromise(store.delete(callId));
};

// ─── Call metadata operations ───────────────────

export const saveCallMeta = async (callId, meta) => {
    const { store } = await withStore(STORE_CALL_META, 'readwrite');
    return requestToPromise(store.put({ callId, ...meta, savedAt: Date.now() }));
};

export const getAllCallMeta = async () => {
    const { store } = await withStore(STORE_CALL_META, 'readonly');
    return requestToPromise(store.getAll());
};

export const deleteCallMeta = async (callId) => {
    const { store } = await withStore(STORE_CALL_META, 'readwrite');
    return requestToPromise(store.delete(callId));
};

// ─── Orphaned chunk recovery ────────────────────

export const finalizeOrphanedChunks = async () => {
    const { store: chunkStore } = await withStore(STORE_CHUNKS, 'readonly');
    const allChunks = await requestToPromise(chunkStore.getAll());
    if (!allChunks.length) return [];

    const callIdSet = new Set(allChunks.map((c) => c.callId));
    const finalized = [];

    // Fetch all meta once to avoid opening a new transaction per iteration
    const { store: metaStore } = await withStore(STORE_CALL_META, 'readonly');
    const allMeta = await requestToPromise(metaStore.getAll());
    const metaMap = new Map(allMeta.map((m) => [m.callId, m]));

    for (const callId of callIdSet) {
        const existing = await getRecording(callId);
        if (existing) continue;

        const meta = metaMap.get(callId);

        const result = await finalizeRecording(callId, {
            corpId: meta?.corpId || '',
            projectId: meta?.projectId || '',
            userId: meta?.userId || '',
            ticketId: meta?.ticketId || '',
            chatThreadId: meta?.chatThreadId || '',
            aiTrainingChatThreadId: meta?.aiTrainingChatThreadId || '',
            aiOperatorTicketId: meta?.aiOperatorTicketId || '',
            callStartTime: meta?.callStartTime || null,
            filename: meta?.ticketId ? `transcript_audio_${meta.ticketId}.webm` : `recording_${callId}.webm`,
        });

        if (result) finalized.push(callId);
    }

    return finalized;
};
